import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, ListPlus, Paperclip, FileText, Upload, Link2, Ban, Undo2 } from "lucide-react";
import { RetireDialog } from "@/components/RecordEditor";
import { ModuleNav } from "./ModuleNav";

const EMPTY = {
  reference: "", requirement: "", department: "Histology", compliance_status: "In progress",
  assigned_to_id: "", due_date: "", notes: "", evidence: "", sop_document_ids: [],
};

const STATUS_STYLE = {
  Compliant: "bg-[#134e4a] text-white",
  "Non-compliant": "bg-red-800 text-white",
  "In progress": "bg-amber-100 text-amber-900",
  "Not applicable": "bg-slate-200 text-slate-700",
};

const SopPicker = ({ docs, selected, onToggle }) => {
  const [q, setQ] = useState("");
  const shown = docs.filter((d) =>
    !q || `${d.control_number} ${d.title}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="border border-slate-200" data-testid="sop-picker">
      <div className="border-b border-slate-200 p-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="sop-picker-search"
               placeholder="Search PolicyTraX by control number or title" className="h-10 border-slate-300" />
      </div>
      <div className="max-h-44 overflow-y-auto">
        {shown.length === 0 && <p className="p-3 text-sm text-slate-500">No documents match.</p>}
        {shown.map((d) => (
          <label key={d.id} className="flex cursor-pointer items-start gap-2 border-b border-slate-100 px-2 py-1.5 text-sm hover:bg-slate-50">
            <input type="checkbox" checked={selected.includes(d.id)} onChange={() => onToggle(d.id)}
                   data-testid={`sop-option-${d.id}`} className="mt-1 h-4 w-4 accent-[#134e4a]" />
            <span>
              <span className="font-mono text-xs font-semibold text-[#134e4a]">{d.control_number}</span>{" "}
              <span className="text-slate-800">{d.title}</span>
              <span className="ml-1 text-[11px] text-slate-500">({d.status}{d.current_version ? ` · Rev ${d.current_version}` : ""})</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default function Checklist() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [sops, setSops] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    q: "", department: params.get("department") || "", status: "", assigned_to_id: "", flag: "", retired: "",
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [detail, setDetail] = useState(null);
  const [retireItem, setRetireItem] = useState(null);
  const [edit, setEdit] = useState({ ...EMPTY });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const { data } = await api.get("/cap-items", { params: p });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => {
    api.get("/accreditation/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/documents").then((r) => setSops(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (filters.department) setParams({ department: filters.department }, { replace: true });
    else setParams({}, { replace: true });
  }, [filters.department, setParams]);

  const role = user?.role;
  const canCreate = ["admin", "lab_director", "supervisor", "pathologist"].includes(role);
  const canTouch = role !== "viewer";

  const create = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      const body = { ...form, due_date: form.due_date || null, assigned_to_id: form.assigned_to_id || null };
      await api.post("/cap-items", body);
      toast.success(`${form.reference.toUpperCase()} added to the checklist`);
      setOpen(false);
      setForm({ ...EMPTY });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/cap-items/${id}`);
      setDetail(data);
      setEdit({
        reference: data.reference, requirement: data.requirement, department: data.department,
        compliance_status: data.compliance_status, assigned_to_id: data.assigned_to_id || "",
        due_date: (data.due_date || "").slice(0, 10), notes: data.notes || "",
        evidence: data.evidence || "", sop_document_ids: data.sop_document_ids || [],
      });
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      const body = role === "tech"
        ? { compliance_status: edit.compliance_status, notes: edit.notes, evidence: edit.evidence }
        : { ...edit, due_date: edit.due_date || null, assigned_to_id: edit.assigned_to_id || null };
      const { data } = await api.put(`/cap-items/${detail.id}`, body);
      setDetail(data);
      toast.success("Checklist item updated");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/cap-items/${detail.id}/attachments?caption=${encodeURIComponent(file.name)}`, fd);
      toast.success("Evidence attached");
      openDetail(detail.id);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const loadStarters = async () => {
    setBusy("starters");
    try {
      const { data } = await api.post("/cap-items/load-starters");
      toast.success(`${data.created.length} checklist items loaded`
        + (data.skipped.length ? ` · ${data.skipped.length} already present` : ""));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const toggleSop = (setter) => (id) => setter((f) => ({
    ...f,
    sop_document_ids: f.sop_document_ids.includes(id)
      ? f.sop_document_ids.filter((x) => x !== id)
      : [...f.sop_document_ids, id],
  }));

  return (
    <div className="space-y-6" data-testid="cap-checklist-page">
      <ModuleNav
        title="Checklist Manager"
        blurb="Every CAP checklist requirement the lab owns, who is responsible, when it is due and the SOPs and evidence that prove it."
        actions={canCreate && (
          <Button onClick={() => setOpen(true)} data-testid="new-cap-item-button"
                  className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Plus size={16} className="mr-2" /> Add checklist item
          </Button>
        )}
      />

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-[200px] flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Search</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   data-testid="cap-search-input" placeholder="Reference or requirement text"
                   className="h-11 border-slate-300 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
          <select value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                  data-testid="cap-department-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All departments</option>
            {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  data-testid="cap-status-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Any status</option>
            {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Assigned to</Label>
          <select value={filters.assigned_to_id} onChange={(e) => setFilters((f) => ({ ...f, assigned_to_id: e.target.value }))}
                  data-testid="cap-assignee-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Anyone</option>
            {(meta?.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Flag</Label>
          <select value={filters.flag} onChange={(e) => setFilters((f) => ({ ...f, flag: e.target.value }))}
                  data-testid="cap-flag-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">No flag</option>
            <option value="overdue">Overdue</option>
            <option value="at_risk">Due soon</option>
            <option value="unassigned">Unassigned</option>
            <option value="no_sop">No SOP linked</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">View</Label>
          <select value={filters.retired} onChange={(e) => setFilters((f) => ({ ...f, retired: e.target.value }))}
                  data-testid="cap-retired-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">Active checklist</option>
            <option value="true">Retired items</option>
          </select>
        </div>
        <Button variant="outline" data-testid="cap-clear-filters" className="h-11 border-slate-300"
                onClick={() => setFilters({ q: "", department: "", status: "", assigned_to_id: "", flag: "", retired: "" })}>
          Clear
        </Button>
      </div>

      <div className="border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm" data-testid="cap-items-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2">Reference</th><th className="px-3 py-2">Requirement</th>
                <th className="px-3 py-2">Department</th><th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Assigned</th><th className="px-3 py-2">Due</th>
                <th className="px-3 py-2">SOPs</th><th className="px-3 py-2">Evidence</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`cap-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => openDetail(r.id)} data-testid={`cap-open-${r.id}`}
                            className="font-mono text-xs font-bold text-[#134e4a] hover:underline">{r.reference}</button>
                  </td>
                  <td className="max-w-[380px] px-3 py-2 text-slate-700">{r.requirement}</td>
                  <td className="px-3 py-2 text-slate-600">{r.department}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.compliance_status]}`}>
                      {r.compliance_status}
                    </span>
                    {r.nonconformance_count > 0 && (
                      <span className="ml-1 bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
                        {r.nonconformance_count} CAPA
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.assigned_to || <span className="text-slate-400">unassigned</span>}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.due_date ? (
                      <span className={r.overdue ? "font-semibold text-red-800" : r.at_risk ? "font-semibold text-amber-800" : "text-slate-600"}>
                        {fmtDate(r.due_date)}
                        {r.overdue && ` · ${Math.abs(r.days_until_due)}d overdue`}
                        {r.at_risk && ` · in ${r.days_until_due}d`}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {r.sops.length === 0 ? <span className="text-xs text-slate-400">none</span> : (
                      <div className="flex flex-wrap gap-1">
                        {r.sops.map((s) => (
                          <Link key={s.id} to={`/documents/${s.id}`} data-testid={`cap-sop-link-${s.id}`}
                                className="bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-[#134e4a] hover:underline">
                            {s.control_number}
                          </Link>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {r.attachment_count > 0
                      ? <span className="inline-flex items-center gap-1"><Paperclip size={12} /> {r.attachment_count}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="px-3 py-10 text-center" data-testid="cap-items-empty">
            <p className="text-sm text-slate-500">
              {Object.values(filters).some(Boolean) ? "No checklist items match these filters." : "No CAP checklist items yet."}
            </p>
            {canCreate && !Object.values(filters).some(Boolean) && (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Button onClick={() => setOpen(true)} data-testid="empty-add-cap-item"
                        className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Plus size={16} className="mr-2" /> Add your first item
                </Button>
                <Button variant="outline" disabled={busy === "starters"} onClick={loadStarters}
                        data-testid="cap-load-starters-button" className="h-11 border-slate-300">
                  <ListPlus size={16} className="mr-2" />
                  {busy === "starters" ? "Loading…" : "Load starter checklist"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-cap-item-dialog">
          <DialogHeader>
            <DialogTitle>Add CAP checklist item</DialogTitle>
            <DialogDescription>Record the requirement, who owns it and the SOPs that satisfy it.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Checklist reference</Label>
                <Input value={form.reference} required minLength={2} placeholder="e.g. ANP.12175"
                       onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                       data-testid="cap-reference-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
                <select value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                        data-testid="cap-department-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Requirement</Label>
              <Textarea value={form.requirement} required minLength={5}
                        onChange={(e) => setForm((f) => ({ ...f, requirement: e.target.value }))}
                        data-testid="cap-requirement-input" className="mt-1 min-h-[80px] border-slate-300"
                        placeholder="What the checklist requires, in the lab's own words." />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Compliance status</Label>
                <select value={form.compliance_status} onChange={(e) => setForm((f) => ({ ...f, compliance_status: e.target.value }))}
                        data-testid="cap-status-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Assigned to</Label>
                <select value={form.assigned_to_id} onChange={(e) => setForm((f) => ({ ...f, assigned_to_id: e.target.value }))}
                        data-testid="cap-assignee-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  <option value="">Unassigned</option>
                  {(meta?.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                       data-testid="cap-due-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs uppercase tracking-wide text-slate-600">Link SOPs from PolicyTraX</Label>
              <SopPicker docs={sops} selected={form.sop_document_ids} onToggle={toggleSop(setForm)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        data-testid="cap-notes-input" className="mt-1 min-h-[60px] border-slate-300" />
            </div>
            <Button type="submit" disabled={busy === "create"} data-testid="create-cap-item-submit"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "create" ? "Saving…" : "Add to checklist"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="cap-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">{detail?.reference}</DialogTitle>
            <DialogDescription>{detail?.department} · added by {detail?.created_by || "—"}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <form onSubmit={save} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Requirement</Label>
                  <Textarea value={edit.requirement} disabled={role === "tech" || !canTouch}
                            onChange={(e) => setEdit((f) => ({ ...f, requirement: e.target.value }))}
                            data-testid="cap-detail-requirement" className="mt-1 min-h-[70px] border-slate-300" />
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Compliance status</Label>
                    <select value={edit.compliance_status} disabled={!canTouch}
                            onChange={(e) => setEdit((f) => ({ ...f, compliance_status: e.target.value }))}
                            data-testid="cap-detail-status" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                      {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Assigned to</Label>
                    <select value={edit.assigned_to_id} disabled={role === "tech" || !canTouch}
                            onChange={(e) => setEdit((f) => ({ ...f, assigned_to_id: e.target.value }))}
                            data-testid="cap-detail-assignee" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                      <option value="">Unassigned</option>
                      {(meta?.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600">Due date</Label>
                    <Input type="date" value={edit.due_date} disabled={role === "tech" || !canTouch}
                           onChange={(e) => setEdit((f) => ({ ...f, due_date: e.target.value }))}
                           data-testid="cap-detail-due" className="mt-1 h-11 border-slate-300" />
                  </div>
                </div>
                {role !== "tech" && (
                  <div>
                    <Label className="mb-1 block text-xs uppercase tracking-wide text-slate-600">
                      <Link2 size={12} className="mr-1 inline" /> Linked SOPs (PolicyTraX)
                    </Label>
                    <SopPicker docs={sops} selected={edit.sop_document_ids} onToggle={toggleSop(setEdit)} />
                  </div>
                )}
                {detail.sops.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="cap-detail-sops">
                    {detail.sops.map((s) => (
                      <Link key={s.id} to={`/documents/${s.id}`}
                            className="inline-flex items-center gap-1 border border-slate-200 px-2 py-1 text-xs text-[#134e4a] hover:border-[#134e4a]">
                        <FileText size={12} /> {s.control_number} — {s.title}
                      </Link>
                    ))}
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Evidence summary</Label>
                  <Textarea value={edit.evidence} disabled={!canTouch}
                            onChange={(e) => setEdit((f) => ({ ...f, evidence: e.target.value }))}
                            data-testid="cap-detail-evidence" className="mt-1 min-h-[60px] border-slate-300"
                            placeholder="Where an inspector can see this being done." />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
                  <Textarea value={edit.notes} disabled={!canTouch}
                            onChange={(e) => setEdit((f) => ({ ...f, notes: e.target.value }))}
                            data-testid="cap-detail-notes" className="mt-1 min-h-[60px] border-slate-300" />
                </div>
                {canTouch && (
                  <div className="flex flex-wrap gap-2">
                    <Button type="submit" disabled={busy === "save"} data-testid="cap-detail-save"
                            className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                      {busy === "save" ? "Saving…" : "Save changes"}
                    </Button>
                    {canCreate && (detail.retired ? (
                      <Button type="button" variant="outline" data-testid="cap-restore-button" className="h-11 border-slate-300"
                              onClick={async () => {
                                try {
                                  const { data } = await api.post(`/cap-items/${detail.id}/restore`);
                                  setDetail(data);
                                  toast.success("Checklist item reinstated");
                                  load();
                                } catch (e) { toast.error(apiError(e)); }
                              }}>
                        <Undo2 size={15} className="mr-2" /> Reinstate
                      </Button>
                    ) : (
                      <Button type="button" variant="outline" onClick={() => setRetireItem(detail)}
                              data-testid="cap-retire-button" className="h-11 border-red-300 text-red-800 hover:bg-red-50">
                        <Ban size={15} className="mr-2" /> Retire item
                      </Button>
                    ))}
                  </div>
                )}
              </form>

              <section className="border border-slate-200">
                <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <Paperclip size={14} className="text-[#134e4a]" />
                  <h3 className="text-sm font-semibold text-slate-900">Supporting documents</h3>
                  {canTouch && (
                    <label className="ml-auto inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-[#134e4a]">
                      <Upload size={13} /> {busy === "upload" ? "Uploading…" : "Attach file"}
                      <input type="file" className="hidden" data-testid="cap-attach-input"
                             onChange={(e) => upload(e.target.files?.[0])} />
                    </label>
                  )}
                </header>
                {(detail.attachments || []).length === 0 ? (
                  <p className="px-3 py-4 text-sm text-slate-500">Nothing attached yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {detail.attachments.map((a) => (
                      <li key={a.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
                          data-testid={`cap-attachment-${a.id}`}>
                        <a href={`${API}/cap-attachments/${a.id}/file`} target="_blank" rel="noreferrer"
                           className="text-[#134e4a] hover:underline">{a.caption}</a>
                        <span className="ml-auto text-[11px] text-slate-500">{a.uploaded_by} · {fmtDate(a.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {(detail.nonconformances || []).length > 0 && (
                <section className="border border-slate-200" data-testid="cap-detail-ncs">
                  <header className="border-b border-slate-200 bg-slate-50 px-3 py-2">
                    <h3 className="text-sm font-semibold text-slate-900">Linked nonconformances</h3>
                  </header>
                  <ul className="divide-y divide-slate-100">
                    {detail.nonconformances.map((n) => (
                      <li key={n.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                        <Link to="/accreditation/nonconformance" className="font-mono text-xs font-semibold text-[#134e4a] hover:underline">{n.nc_number}</Link>
                        <span className="min-w-0 flex-1 truncate">{n.title}</span>
                        <span className="text-[11px] text-slate-500">{n.status}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
